
import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import ConfirmationModal from '../components/ConfirmationModal';
import UploadPhotoModal from '../components/UploadPhotoModal';
import AvatarPickerModal from '../components/AvatarPickerModal';
import { useWeb3 } from '../contexts/Web3Context';
import { httpsCallable } from "firebase/functions";
import { getAuth, EmailAuthProvider, reauthenticateWithCredential, updatePassword } from "firebase/auth";
import { functions } from '../firebaseConfig';
import ChangePasswordModal from '../components/ChangePasswordModal';
import EditProfileModal from '../components/EditProfileModal';
import UploadQRModal from '../components/UploadQRModal';
import { db, storage, auth } from '../firebaseConfig';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useSearchParams } from 'react-router-dom';

const ProfileView = () => {
  const { userData, updateUserData, loading } = useUser();
  const { wallet, connectWallet, unbindWallet, manualBindWallet } = useWeb3();

  // Estados de los modales
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);
  const [isUploadQRModalOpen, setIsUploadQRModalOpen] = useState(false);

  // Estados de carga y error
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    // Lógica para abrir el modal del Formulario de Edición
    if (searchParams.get('openModal') === 'true') {
      setIsEditProfileModalOpen(true);
      setSearchParams({}, { replace: true });
    }

    // Lógica para abrir el nuevo modal de QR
    if (searchParams.get('openQR') === 'true') {
      setIsUploadQRModalOpen(true);
      setSearchParams({}, { replace: true });
    }

  }, [searchParams, setSearchParams]);

  // Limpia los mensajes de error/éxito
  const clearMessages = () => {
    setError(null);
    setSuccessMessage(null);
  };

  const formatAddress = (address) => {
    if (!address) return "";
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  const handleDeleteAccount = async () => {
    clearMessages();
    setCargando(true);
    try {
      const eliminarCuenta = httpsCallable(functions, 'eliminarCuentaUsuario');
      await eliminarCuenta();
      window.location.href = '/login';
    } catch (err) {
      console.error("Error al eliminar la cuenta:", err);
      setError("No se pudo eliminar la cuenta. Inténtalo de nuevo.");
      setCargando(false);
    }
  };

  // Función de guardado de perfil (para QR y Perfil)
  const handleSaveProfile = async (profileData, qrFile) => {
    clearMessages();
    setCargando(true);
    let qrCodeUrl = userData.qrCodeUrl || null;

    try {
      const gestionarPerfil = httpsCallable(functions, 'gestionarPerfilDeUsuario');

      // La lógica de subir el archivo es la misma que ya tenías
      if (qrFile) {
        const user = auth.currentUser;
        if (!user) throw new Error("Usuario no encontrado");

        const storageRef = ref(storage, `qrcodes/${user.uid}/${qrFile.name}`);
        const snapshot = await uploadBytes(storageRef, qrFile);
        qrCodeUrl = await getDownloadURL(snapshot.ref);
      }

      const dataParaActualizar = {
        ...profileData,
        qrCodeUrl: qrCodeUrl,
        haContratado: true // Marcamos que el servicio ha sido contratado/activado
      };

      await gestionarPerfil(dataParaActualizar);
      updateUserData(dataParaActualizar);

      setSuccessMessage("¡Código QR subido con éxito! Tu servicio está activo.");
      setIsEditProfileModalOpen(false);
      setIsUploadQRModalOpen(false);

    } catch (err) {
      console.error("Error al actualizar el perfil o subir QR:", err);
      setError("No se pudo subir el código QR.");
    } finally {
      setCargando(false);
    }
  };

  const handleUpdateAvatar = async (newUrl) => {
    if (newUrl === userData?.avatarUrl) {
      setIsUploadModalOpen(false);
      setIsAvatarModalOpen(false);
      return;
    }
    clearMessages();
    setCargando(true);
    try {
      const gestionarPerfil = httpsCallable(functions, 'gestionarPerfilDeUsuario');
      await gestionarPerfil({ fotoURL: newUrl });
      updateUserData({ avatarUrl: newUrl });
      setSuccessMessage("¡Avatar actualizado con éxito!");
    } catch (err) {
      console.error("Error al actualizar el avatar:", err);
      setError("No se pudo actualizar el avatar. Inténtalo de nuevo.");
    } finally {
      setCargando(false);
      setIsUploadModalOpen(false);
      setIsAvatarModalOpen(false);
    }
  };

  const handleChangePassword = (currentPassword, newPassword) => {
    clearMessages();
    setCargando(true);
    const auth = getAuth();
    const user = auth.currentUser;
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    return reauthenticateWithCredential(user, credential)
      .then(() => {
        return updatePassword(user, newPassword);
      })
      .then(() => {
        setSuccessMessage("¡Contraseña actualizada con éxito!");
        setIsChangePasswordModalOpen(false);
        setCargando(false);
      })
      .catch((err) => {
        console.error("Error al cambiar la contraseña:", err);
        let userMessage = "No se pudo actualizar la contraseña.";
        if (err.code === 'auth/wrong-password') {
          userMessage = "La contraseña actual es incorrecta.";
        } else if (err.code === 'auth/weak-password') {
          userMessage = "La nueva contraseña debe tener al menos 6 caracteres.";
        }
        setCargando(false);
        throw new Error(userMessage);
      });
  };

  const handleConfirmDisconnect = () => {
    unbindWallet();
    setIsDisconnectModalOpen(false);
  };

  if (loading) {
    return (
      <div className="profile-view">
        <h2>Mi Perfil</h2>
        <p>Cargando información del usuario...</p>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="profile-view">
        <h2>Error</h2>
        <div className="alert alert-danger">
          No se pudo cargar el perfil. Por favor, <a href="/login">inicia sesión</a> de nuevo.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="profile-view">
        <h2>Mi Perfil</h2>
        {error && <div className="alert alert-danger" role="alert" onClick={clearMessages} style={{ cursor: 'pointer' }}>{error}</div>}
        {successMessage && <div className="alert alert-success" role="alert" onClick={clearMessages} style={{ cursor: 'pointer' }}>{successMessage}</div>}

        <div className="profile-section">
          <div className="profile-header">
            <img src={userData?.avatarUrl || '/assets/default-avatar.png'} alt="Avatar de usuario" className="profile-avatar" />
            <div className="profile-avatar__actions">
              <button className="profile-button" onClick={() => { clearMessages(); setIsUploadModalOpen(true); }}>Cambiar Foto de Perfil</button>
              <button className="profile-button" onClick={() => { clearMessages(); setIsAvatarModalOpen(true); }}>Elegir un Avatar</button>
            </div>
          </div>
        </div>

        <div className="profile-section">
          <h4 className="profile-section__title">Información Personal</h4>
          <div className="profile-row">
            <div className="profile-field">
              <label>Nombre de Usuario</label>
              <p className="profile-field__value">{userData?.username}</p>
            </div>
            <button className="profile-button" onClick={() => { clearMessages(); setIsEditProfileModalOpen(true); }}>
              Editar Perfil
            </button>
          </div>
          <div className="profile-field">
            <label>Nombre Completo</label>
            <p className="profile-field__value">{userData?.fullName || 'No completado'}</p>
          </div>
          <div className="profile-field">
            <label>Correo Electrónico</label>
            <p className="profile-field__value">{userData?.email}</p>
          </div>
        </div>

        <div className="profile-section">
          <h4 className="profile-section__title">Seguridad</h4>
          <div className="profile-row">
            <div className="profile-field">
              <label>Contraseña</label>
              <p className="profile-field__value">••••••••••••</p>
            </div>
            <button className="profile-button" onClick={() => { clearMessages(); setIsChangePasswordModalOpen(true); }}>
              Cambiar Contraseña
            </button>
          </div>
        </div>

        <div className="profile-section">
          <div className="profile-section__header">
            <h4 className="profile-section__title">Billetera Electrónica</h4>
            {wallet && <span className="profile-wallet__date">Conectada a: {wallet.label}</span>}
          </div>
          {userData?.walletAddress ? (
            <>
              <div className="profile-wallet-card">
                <div className="profile-wallet-card__info">
                  <span>Dirección Vinculada: <b>{formatAddress(userData.walletAddress)}</b></span>
                  {wallet && <span>Red: <b>{wallet.chains[0].id === '0x38' ? 'BNB Smart Chain' : 'Ethereum'}</b></span>}
                </div>
                <div className="profile-wallet-card__status">
                  {wallet ? (
                    <><span className="dot verified"></span> Conectado</>
                  ) : (
                    <><span className="dot" style={{ background: '#ff9800' }}></span> Vinculada (Off)</>
                  )}
                </div>
              </div>
              <div className="profile-wallet__actions" style={{ display: 'flex', gap: '10px' }}>
                {!wallet && (
                  <button className="profile-button" onClick={connectWallet} style={{ flex: 1, background: '#4caf50' }}>Conectar</button>
                )}
                <button className="profile-button profile-button--danger" onClick={() => setIsDisconnectModalOpen(true)} style={{ flex: 1 }}>Desvincular</button>
              </div>
            </>
          ) : (
            <>
              <div className="profile-wallet-card">
                <div className="profile-wallet-card__info">
                  <span>No hay ninguna billetera vinculada a tu cuenta.</span>
                </div>
                <div className="profile-wallet-card__status">
                  <span className="dot"></span> Sin Vincular
                </div>
              </div>
              <div className="profile-wallet__actions">
                {wallet ? (
                  <button className="profile-button" onClick={async () => {
                    setCargando(true);
                    try { await manualBindWallet(); window.location.reload(); }
                    catch (e) { console.error(e); }
                    finally { setCargando(false); }
                  }}>Confirmar Vinculación ({formatAddress(wallet.accounts[0].address)})</button>
                ) : (
                  <button className="profile-button" onClick={connectWallet}>Conectar Billetera</button>
                )}
              </div>
            </>
          )}
        </div>

        <div className="profile-footer">
          <button className="profile-button profile-button--danger" onClick={() => { clearMessages(); setIsDeleteModalOpen(true); }}>Eliminar Cuenta</button>
          <button className="profile-button profile-button--primary" disabled={true} style={{ opacity: 0.5, cursor: 'not-allowed' }}>{cargando ? 'Guardando...' : 'Guardar Cambios'}</button>
        </div>
      </div>

      {/* Modales */}
      {isDeleteModalOpen && (
        <ConfirmationModal
          title="¿Estas seguro que quieres eliminar la cuenta?"
          message="(Este proceso no se puede deshacer)"
          confirmText={cargando ? "Eliminando..." : "Eliminar Cuenta"}
          cancelText="Cancelar"
          onConfirm={handleDeleteAccount}
          onCancel={() => setIsDeleteModalOpen(false)}
          isConfirmDisabled={cargando}
        />
      )}
      {isDisconnectModalOpen && (<ConfirmationModal title="¿Desvincular Billetera?" message="La desvinculación borrará la billetera actual de tu perfil, permitiéndote conectar una nueva." confirmText="Sí, desvincular" cancelText="Cancelar" onConfirm={handleConfirmDisconnect} onCancel={() => setIsDisconnectModalOpen(false)} />)}

      {isUploadModalOpen && <UploadPhotoModal onClose={() => setIsUploadModalOpen(false)} onSave={handleUpdateAvatar} />}
      {isAvatarModalOpen && <AvatarPickerModal onClose={() => setIsAvatarModalOpen(false)} onSave={handleUpdateAvatar} />}

      {isChangePasswordModalOpen && (
        <ChangePasswordModal
          onClose={() => setIsChangePasswordModalOpen(false)}
          onSave={handleChangePassword}
        />
      )}

      {isEditProfileModalOpen && (
        <EditProfileModal
          currentUserData={userData}
          onClose={() => setIsEditProfileModalOpen(false)}
          onSave={handleSaveProfile}
          isLoading={cargando}
        />
      )}

      {isUploadQRModalOpen && (
        <UploadQRModal
          currentUserData={userData}
          onClose={() => setIsUploadQRModalOpen(false)}
          onSave={handleSaveProfile} // Reutilizamos la función de guardado
          isLoading={cargando}
        />
      )}
    </>
  );
};

export default ProfileView;